import { ResearchInPracticeSection } from '../components/sections/ResearchInPracticeSection';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Container, Button } from '../components/ui';
import { IndustryCard } from '../components/sections';
import {
  INDUSTRIES,
  INDUSTRIES_MEDIA,
  INDUSTRIES_OVERVIEW_DESCRIPTION,
} from '../data/industries';

/*
 * Industries (a.k.a. "Industries We Empower")
 * -------------------------------------------
 * Landing page for /industries. Same two-zone shape as /services:
 *
 *   1. A cover-video hero (using the "explore by industry" overview
 *      clip the sidebar drawer also uses as its category fallback)
 *      with a white overlay card that greets the visitor and links
 *      to the contact form.
 *
 *   2. A responsive grid — "Industries we empower" — with one card
 *      per industry from `INDUSTRIES`. Each card shows the industry's
 *      cover clip and a Learn-more link to /industries/<slug>.
 */

export const Industries: React.FC = () => {
  return (
    <>
      {/* ═════════════════ Breadcrumbs strip ═════════════════
          Same sticky-under-header treatment as /services and
          /services/<slug> so the trail stays anchored as the visitor
          scrolls through the hero + card grid. */}
      <div style={{ height: '80px' }} aria-hidden />
      <div className="sticky top-20 z-40 bg-white border-b border-navy-100 shadow-sm">
        <Container className="py-4">
          <Breadcrumbs />
        </Container>
      </div>

      {/* ═════════════════ Hero cover ═════════════════ */}
      <IndustriesHero />

      {/* ═════════════════ Industries grid ═════════════════
          Full-bleed grid — same layout / typography as the
          "Explore how we can help" services grid so the two
          "chapter" landing pages read as a matched pair. */}
      <section className="bg-white py-16 md:py-20 lg:py-24">
        <div className="site-wide-container">
          <motion.h2
            className="heading-lg text-navy-900 mb-10 md:mb-14"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            Industries we empower
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 lg:gap-x-10 gap-y-14 md:gap-y-16">
            {INDUSTRIES.map((industry, i) => (
              <IndustryCard
                key={industry.id}
                industry={industry}
                animationDelay={Math.min(i, 5) * 0.06}
              />
            ))}
          </div>
        </div>
      </section>
      {/* Show the full research catalog, as on IstaLabs. */}
      <ResearchInPracticeSection />
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Breadcrumbs: React.FC = () => (
  <nav
    aria-label="Breadcrumb"
    className="flex flex-wrap items-center gap-2 text-sm"
  >
    <Link
      to="/"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      Home
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <span aria-current="page" className="font-semibold text-navy-900">
      Industries
    </span>
  </nav>
);

/**
 * Hero video + overlay card. Uses the "explore by industry" overview
 * clip that also feeds the sidebar drawer's category fallback
 * preview, so the sidebar → landing-page transition feels continuous.
 * Height math + overlay card sizing intentionally match the /services
 * hero so the two landing pages open with identical visual weight.
 */
const IndustriesHero: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  return (
    <section className="relative bg-navy-950">
      <div
        className="responsive-hero relative w-full overflow-hidden"
      >
        <video
          ref={videoRef}
          src={INDUSTRIES_MEDIA.overview}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div aria-hidden className="absolute inset-0 bg-black/35" />

        <div className="relative flex items-center justify-center px-4 sm:px-6 py-10 sm:py-14 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={
              'w-full max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl ' +
              'bg-white rounded-2xl border border-navy-100 ' +
              'shadow-[0_25px_70px_-15px_rgba(2,8,20,0.6)] ' +
              'p-5 sm:p-8 md:p-10 lg:p-12'
            }
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[54px] font-bold text-navy-900 leading-tight mb-4 md:mb-6">
              Industries we empower
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-[22px] text-navy-700 leading-relaxed mb-7 md:mb-9">
              {INDUSTRIES_OVERVIEW_DESCRIPTION}
            </p>
            <Link to="/contact?inquiry=industry">
              <Button
                variant="primary"
                size="lg"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Connect with our experts
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
